import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  Mode,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Checkbox from '@/components/molecules/checkbox/checkbox';
import ComboBox, {
  ComboBoxOption,
} from '@/components/molecules/combo-box/combo-box';
import Input from '@/components/molecules/input/input';
import Radiobutton from '@/components/molecules/radiobutton/radiobutton';
import Select, { SelectOption } from '@/components/molecules/select/select';
import Tooltip, {
  TooltipPosition,
} from '@/components/molecules/tooltip/tooltip';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import CompanionsChipList, {
  CompanionChip,
} from '@/components/organisms/companions-chip-list/companions-chip-list';
import SaveCompanionDetailsModal from '@/components/organisms/save-companion-details-modal/save-companion-details-modal';
import useQuerySearchAddress from '@/integrations/content/addresses/search-address/search-address.hooks';
import {
  BookingFlowCompanionDraft,
  useBookingFlowContext,
} from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useUserProfileContext } from '@/libraries/contexts/user-profile-context/user-profile-context';
import { toSimpleDate } from '@/libraries/formatters/use-date-formatter/simple-date';
import useDebounce from '@/libraries/hooks/use-debounce';
import isDeepEqual from '@/libraries/utils/is-deep-equal';
import validateEmail from '@/libraries/validators/validate-email';
import validateNumber from '@/libraries/validators/validate-number';
import styles from './edit-pax-details.module.scss';

const COUNTRY_ID_DENMARK = 38;
const DEBOUNCE_DELAY = 250;

type Companion = Content.Components['TravelCompanionDto'];

interface CompanionChipItem extends CompanionChip {
  dateOfBirth: string;
  firstName: string;
  lastName: string;
}

const getDateTimestamp = (date?: string | null) => {
  if (!date) return null;

  const timestamp = Date.parse(date);

  return Number.isNaN(timestamp) ? null : timestamp;
};

const normalizeValue = (value: string) => value.trim().toLowerCase();

export type EditedPassenger = {
  additionalFields: {
    hasSaveCompanionChanges?: boolean;
  };
  originalFields: Partial<Content.Properties.Passenger>;
  uniquePaxHash: string;
  updatedFields: Partial<Content.Properties.Passenger>;
};

export interface EditPaxDetailsProps {
  /** Text to show for agree send email checkbox */
  agreeSendEmailText?: string;
  /** Text to show for agree send sms checkbox */
  agreeSendSMSText?: string;
  /** The available address countries */
  availableAddressCountries?: Content.Properties.AddressCountry[];
  /** The class name to apply */
  className?: string;
  /** Whether editing the date of birth is disabled */
  disableEditDoB?: boolean;
  /** Whether editing the names is disabled */
  disableEditNames?: boolean;
  /** Text to show as disclaimer */
  disclaimerText?: string;
  /** Whether email and phone are mandatory */
  emailAndPhoneMandatory?: boolean;
  /** The first passenger last name to set for others */
  firstPassengerLastName?: string;
  /** Whether to show the address input */
  hideAddress?: boolean;
  /** Whether to show the age input */
  hideAge?: boolean;
  /** Whether to show agree send email checkbox */
  hideAgreeSendEmail?: boolean;
  /** Whether to show agree send sms checkbox */
  hideAgreeSendSMS?: boolean;
  /** Whether to show the avatar and name display */
  hideAvatar?: boolean;
  /** Whether to show the email input */
  hideEmail?: boolean;
  /** Whether to show the phone input */
  hidePhone?: boolean;
  /** Indicates whether to allow travel planner toggle */
  hideTravelPlannerToggle?: boolean;
  /** Whether the passenger appears on top of the room */
  isFirstPassenger?: boolean;
  /** Indicates whether the component is in a loading state */
  isLoading?: boolean;
  /** An event that is triggered on change */
  onChange?: (editedPassenger: EditedPassenger) => void;
  /** Callback for selecting or removing a companion */
  onSelectCompanion?: (companionId: string | null) => void;
  /** An event that is triggered on travel planner toggle */
  onTravelPlannerToggle?: (uniquePaxHash: string) => void;
  /** An event that is triggered on validation */
  onValidation?: (value: boolean) => void;
  /** The single passenger information */
  passenger?: Content.Properties.Passenger;
  /** Selected companion id for this slot */
  selectedCompanionId?: string;
  /** Whether to clear the edited data */
  shouldClear?: boolean;
  /** Indicates whether to highlight the errors */
  shouldHighlightErrors?: boolean;
  /** The position of the tooltip */
  tooltipPosition?: TooltipPosition;
  /** The uniquePaxHash of the passenger as travel planner  */
  travelPlannerId?: string;
  /** Already selected companion ids in other slots */
  usedCompanionIds?: string[];
  /** The validation mode for the form */
  validationMode?: Mode;
}

interface FormValues {
  address: string;
  agreeSendEmail: boolean;
  agreeSendSMS: boolean;
  countryId: number;
  dateOfBirth: string;
  email: string;
  firstName: string;
  gender: string;
  isCustomer: boolean;
  lastName: string;
  phone: string;
  phonePrefix: string;
  postCode: string;
  postCodePlace: string;
}

const EditPaxDetails = ({
  agreeSendEmailText,
  agreeSendSMSText,
  availableAddressCountries = [],
  className,
  disableEditDoB = false,
  disableEditNames = false,
  disclaimerText,
  emailAndPhoneMandatory = false,
  firstPassengerLastName,
  hideAddress = false,
  hideAge = false,
  hideAgreeSendEmail = false,
  hideAgreeSendSMS = false,
  hideAvatar = false,
  hideEmail = false,
  hidePhone = false,
  hideTravelPlannerToggle = false,
  isFirstPassenger = false,
  isLoading = false,
  onChange,
  onSelectCompanion,
  onTravelPlannerToggle,
  onValidation,
  passenger,
  shouldClear = false,
  shouldHighlightErrors = false,
  tooltipPosition,
  travelPlannerId,
  selectedCompanionId,
  usedCompanionIds = [],
  validationMode = 'onBlur',
}: EditPaxDetailsProps) => {
  const { t } = useI18n();
  const isFormInitialized = useRef(false);
  const { setCompanionToCreate, stepsData } = useBookingFlowContext();
  const { companions } = useUserProfileContext();
  const { bookingType } = usePageDetailsContext();
  const [addressSuggestions, setAddressSuggestions] = useState<
    Content.Properties.SearchAddressSuggestion[]
  >([]);
  const [showSaveCompanionInfoModal, setShowSaveCompanionInfoModal] =
    useState(false);
  const [localSelectedCompanionId, setLocalSelectedCompanionId] = useState<
    string | null
  >(selectedCompanionId ?? null);
  const [editedPassenger, setEditedPassenger] = useState<EditedPassenger>({
    additionalFields: { hasSaveCompanionChanges: false },
    originalFields: {},
    uniquePaxHash: passenger?.uniquePaxHash ?? '',
    updatedFields: {},
  });
  const initialSaveCompanionCheckedRef = useRef(false);

  const effectiveSelectedCompanionId = localSelectedCompanionId;
  const currentCompanionDraft = passenger?.uniquePaxHash
    ? stepsData.travelers.companionsToCreateByPaxHash?.[passenger.uniquePaxHash]
    : undefined;
  const isSaveCompanionChecked = !!currentCompanionDraft;
  const isSaveCompanionDisabled = !!effectiveSelectedCompanionId;

  useEffect(() => {
    setLocalSelectedCompanionId(selectedCompanionId ?? null);
  }, [selectedCompanionId]);

  const showTravelPlannerToggle =
    passenger &&
    (passenger.gender === 'male' || passenger.gender === 'female') &&
    !hideTravelPlannerToggle;

  const isTravelPlanner =
    !!travelPlannerId && travelPlannerId === passenger?.uniquePaxHash;

  const shouldShowSaveCompanionToggle =
    !!passenger &&
    !passenger.isCustomer &&
    !isTravelPlanner &&
    bookingType === 'PreBooking';

  const isTravelPlannerLocked = isTravelPlanner && showTravelPlannerToggle;

  const hideAddressSection =
    !passenger?.travelInsurance.insuranceRequiresPassDetails || hideAddress;
  const hideEmailInput =
    hideEmail || (passenger && passenger.gender === 'infant');
  const hideAgreeSendEmailInput =
    hideAgreeSendEmail || (passenger && passenger.gender === 'infant');
  const hidePhoneInput =
    hidePhone || (passenger && passenger.gender === 'infant');
  const hideAgreeSendSMSInput =
    hideAgreeSendSMS || (passenger && passenger.gender === 'infant');

  const hasContactSection =
    !hideEmailInput ||
    !hideAgreeSendEmailInput ||
    !hidePhoneInput ||
    !hideAgreeSendSMSInput;

  // Count how many sections are visible
  const visibleSectionsCount = [
    !hideAge, // Basic info section
    hasContactSection, // Contact section
    !hideAddressSection, // Address section
  ].filter(Boolean).length;

  const showSectionHeadline = visibleSectionsCount > 1;

  const countryOptions: SelectOption[] = useMemo(() => {
    if (!availableAddressCountries) return [];

    return availableAddressCountries.map(({ countryId, countryName }) => ({
      name: countryName,
      value: countryId,
    }));
  }, [availableAddressCountries]);

  const addressOptions: ComboBoxOption[] = useMemo(() => {
    if (!addressSuggestions) return [];

    return addressSuggestions.map(
      (addressSuggestion, addressSuggestionIndex) => {
        const addressText = [
          addressSuggestion.address,
          addressSuggestion.postCode,
          addressSuggestion.postCodePlace,
          addressSuggestion.countryName,
        ]
          .filter(Boolean)
          .join(', ');

        return {
          id: addressSuggestionIndex,
          inputName: addressSuggestion.address || '',
          optionName: addressText,
          value: addressSuggestion.address || '',
        };
      },
    );
  }, [addressSuggestions]);

  const phonePrefixOptions: SelectOption[] = useMemo(() => {
    if (!availableAddressCountries) return [];

    return availableAddressCountries.map(({ countryPhonePrefix }) => ({
      name: countryPhonePrefix,
      value: countryPhonePrefix,
    }));
  }, [availableAddressCountries]);

  const defaultValues = {
    address: '',
    agreeSendEmail: false,
    agreeSendSMS: false,
    countryId: 0,
    dateOfBirth: '',
    email: '',
    firstName: '',
    gender: '',
    isCustomer: false,
    lastName: '',
    phone: '',
    phonePrefix: '',
    postCode: '',
    postCodePlace: '',
  };

  const { control, formState, register, reset, setValue, trigger, watch } =
    useForm<FormValues>({
      defaultValues: defaultValues,
      mode: validationMode,
    });

  const formValues = watch();
  const watchedDateOfBirth = watch('dateOfBirth');
  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');
  const watchedAddress = useWatch({ control, name: 'address' });
  const watchedCountryId = useWatch({ control, name: 'countryId' });
  const watchedPostCode = useWatch({ control, name: 'postCode' });
  const watchedPostCodePlace = useWatch({ control, name: 'postCodePlace' });

  const { value: debouncedAddress } = useDebounce(
    watchedAddress,
    DEBOUNCE_DELAY,
  );
  const { value: debouncedPostCode } = useDebounce(
    watchedPostCode,
    DEBOUNCE_DELAY,
  );

  const saveCompanionDraft: BookingFlowCompanionDraft = useMemo(
    () => ({
      dateOfBirth: watchedDateOfBirth || '',
      firstName: watchedFirstName.trim(),
      lastName: watchedLastName.trim(),
    }),
    [watchedDateOfBirth, watchedFirstName, watchedLastName],
  );

  const hasSelectedCountry = !!watchedCountryId;

  const { data: dataSearchAddress } = useQuerySearchAddress(
    {
      address: debouncedAddress,
      countryId: Number(watchedCountryId),
    },
    {
      enabled:
        Number(watchedCountryId) === COUNTRY_ID_DENMARK && !!debouncedAddress,
    },
  );
  const { data: dataSearchPostCode } = useQuerySearchAddress(
    {
      countryId: Number(watchedCountryId),
      postCode: debouncedPostCode,
    },
    {
      enabled: hasSelectedCountry && !!debouncedPostCode,
    },
  );

  const {
    field: { onChange: onIsCustomerChange },
  } = useController({
    control,
    name: 'isCustomer',
  });

  const {
    field: {
      value: agreeSendEmailValue,
      onChange: onAgreeSendEmailChange,
      ref: agreeSendEmailRef,
      name: agreeSendEmail,
    },
  } = useController({
    control,
    name: 'agreeSendEmail',
  });

  const {
    field: {
      value: agreeSendSMSValue,
      onChange: onAgreeSendSMSChange,
      ref: agreeSendSMSRef,
      name: agreeSendSMS,
    },
  } = useController({
    control,
    name: 'agreeSendSMS',
  });

  const handleAgreeSendEmailChange = () => {
    onAgreeSendEmailChange(!agreeSendEmailValue);
  };

  const handleAgreeSendSMSChange = () => {
    onAgreeSendSMSChange(!agreeSendSMSValue);
  };

  const getErrorMessage = (
    fieldName: keyof FormValues,
    required?: boolean,
  ): string | undefined => {
    if (shouldHighlightErrors && !formValues[fieldName] && required) {
      return t('items.form.validationRequired');
    }

    const error = formState.errors[fieldName];

    if (!error) return;

    return error.message;
  };

  const getBasicPerson = (): BasicPerson => {
    if (!passenger)
      return {
        firstName: '',
        lastName: '',
      };

    if (!watchedFirstName && !watchedLastName) {
      return {
        firstName: passenger.placeholders.firstName,
        lastName: passenger.placeholders.lastName,
      };
    }

    return {
      firstName: watchedFirstName,
      lastName: watchedLastName,
    };
  };

  const getNameDisplay = (): string => {
    if (!passenger) return '';

    if (!watchedFirstName && !watchedLastName) {
      return `${passenger.placeholders.firstName} ${passenger.placeholders.lastName}`;
    }

    return `${watchedFirstName} ${watchedLastName}`;
  };

  const getCompanionId = (companion: Companion, index: number) =>
    companion.id ||
    `${companion.firstName}-${companion.lastName}-${companion.birthDate}-${index}`;

  const usedCompanionIdsSet = useMemo(
    () => new Set(usedCompanionIds),
    [usedCompanionIds],
  );

  const companionChipItems: CompanionChipItem[] = useMemo(() => {
    return (companions || [])
      .map((companion, index) => {
        return {
          dateOfBirth: toSimpleDate(companion.birthDate),
          firstName: companion.firstName || '',
          id: getCompanionId(companion, index),
          isSelected:
            getCompanionId(companion, index) === effectiveSelectedCompanionId,
          label:
            [companion.firstName, companion.lastName]
              .filter(Boolean)
              .join(' ') ||
            companion.firstName ||
            '',
          lastName: companion.lastName || '',
        };
      })
      .filter(companion => {
        const dateTimestamp = getDateTimestamp(companion.dateOfBirth);

        if (!dateTimestamp) {
          return false;
        }

        const minTimestamp = getDateTimestamp(passenger?.minDateOfBirth);
        const maxTimestamp = getDateTimestamp(passenger?.maxDateOfBirth);

        if (minTimestamp !== null && dateTimestamp < minTimestamp) {
          return false;
        }

        if (maxTimestamp !== null && dateTimestamp > maxTimestamp) {
          return false;
        }

        if (
          effectiveSelectedCompanionId &&
          companion.id === effectiveSelectedCompanionId
        ) {
          return true;
        }

        return !usedCompanionIdsSet.has(companion.id);
      });
  }, [
    companions,
    effectiveSelectedCompanionId,
    passenger?.maxDateOfBirth,
    passenger?.minDateOfBirth,
    usedCompanionIdsSet,
  ]);

  const handleCompanionSelectionChange = (companionId: string | null) => {
    setLocalSelectedCompanionId(companionId);

    onSelectCompanion?.(companionId);
  };

  const handleClickCompanion = (companion: CompanionChipItem) => {
    setValue('firstName', companion.firstName || '');
    setValue('lastName', companion.lastName || '');
    setValue('dateOfBirth', toSimpleDate(companion.dateOfBirth));

    trigger(['firstName', 'lastName', 'dateOfBirth']);

    handleCompanionSelectionChange(companion.id);
  };

  const handleClickRemoveCompanion = () => {
    setValue('firstName', '');
    setValue('lastName', '');
    setValue('dateOfBirth', '');

    trigger(['firstName', 'lastName', 'dateOfBirth']);

    handleCompanionSelectionChange(null);
  };

  const matchedCompanionId = useMemo(() => {
    const normalizedFirstName = normalizeValue(watchedFirstName);
    const normalizedLastName = normalizeValue(watchedLastName);
    const normalizedDateOfBirth = watchedDateOfBirth?.trim() || '';

    if (!normalizedFirstName || !normalizedLastName || !normalizedDateOfBirth) {
      return null;
    }

    return (
      companionChipItems.find(companion => {
        return (
          normalizeValue(companion.firstName) === normalizedFirstName &&
          normalizeValue(companion.lastName) === normalizedLastName &&
          companion.dateOfBirth === normalizedDateOfBirth
        );
      })?.id || null
    );
  }, [
    companionChipItems,
    watchedDateOfBirth,
    watchedFirstName,
    watchedLastName,
  ]);

  const handleClickSaveCompanion = () => {
    if (!passenger?.uniquePaxHash || isSaveCompanionDisabled) return;

    setCompanionToCreate(
      passenger.uniquePaxHash,
      isSaveCompanionChecked ? null : saveCompanionDraft,
    );
  };

  const handleSelectSuggestion = (value: string | number) => {
    if (typeof value === 'string') {
      setValue('address', value);
    }

    if (typeof value === 'number') {
      const option = addressSuggestions[value];

      setValue('postCodePlace', '');

      if (!option) return;

      setValue('address', option.address ? option.address : watchedAddress);
      setValue('postCode', option.postCode ? option.postCode : watchedPostCode);
      setValue(
        'postCodePlace',
        option.postCodePlace ? option.postCodePlace : watchedPostCodePlace,
      );

      // Manually trigger form state update/validation
      trigger(['address', 'postCode', 'postCodePlace']);
    }
  };

  const updateFormValues = () => {
    reset(
      passenger
        ? {
            address: passenger.address,
            agreeSendEmail: passenger.agreeSendEmail,
            agreeSendSMS: passenger.agreeSendSMS,
            countryId: passenger.countryId,
            dateOfBirth: passenger.dateOfBirth,
            email: passenger.email,
            firstName: passenger.firstName,
            gender: passenger.gender,
            lastName: passenger.lastName,
            phone: passenger.phone,
            phonePrefix: passenger.phonePrefix,
            postCode: passenger.postCode,
            postCodePlace: passenger.postCodePlace,
          }
        : defaultValues,
    );
  };

  useEffect(() => {
    if (!isFormInitialized.current) return;

    if (!passenger) return;

    setEditedPassenger(previous => {
      const originalFields: Partial<Content.Properties.Passenger> = {
        ...previous.originalFields,
      };

      const updatedFields: Partial<Content.Properties.Passenger> = {
        ...previous.updatedFields,
      };

      for (const field of Object.keys(
        formValues,
      ) as (keyof Content.Properties.Passenger)[]) {
        const currentValue = formValues[field];
        const originalValue = passenger[field];

        if (currentValue !== originalValue) {
          updatedFields[field] = currentValue;
          originalFields[field] = originalValue as any;
        } else {
          delete updatedFields[field];
        }
      }

      const updatedEditedPassenger = {
        additionalFields: previous.additionalFields,
        originalFields,
        uniquePaxHash: passenger.uniquePaxHash,
        updatedFields,
      };

      if (isDeepEqual(previous, updatedEditedPassenger)) {
        return previous;
      }

      return updatedEditedPassenger;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  useEffect(() => {
    onValidation?.(formState.isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isValid]);

  useEffect(() => {
    if (matchedCompanionId === effectiveSelectedCompanionId) return;

    handleCompanionSelectionChange(matchedCompanionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSelectedCompanionId, matchedCompanionId]);

  useEffect(() => {
    if (!isFormInitialized.current || !passenger) return;

    const hasSaveCompanionChanges =
      isSaveCompanionChecked !== initialSaveCompanionCheckedRef.current;

    setEditedPassenger(previous => {
      if (
        previous.additionalFields.hasSaveCompanionChanges ===
        hasSaveCompanionChanges
      ) {
        return previous;
      }

      return {
        ...previous,
        additionalFields: { hasSaveCompanionChanges },
      };
    });
  }, [isSaveCompanionChecked, passenger]);

  useEffect(() => {
    if (!passenger?.uniquePaxHash) return;

    if (isSaveCompanionDisabled) {
      if (currentCompanionDraft) {
        setCompanionToCreate(passenger.uniquePaxHash, null);
      }

      return;
    }

    if (!isSaveCompanionChecked) return;

    if (isDeepEqual(currentCompanionDraft, saveCompanionDraft)) return;

    setCompanionToCreate(passenger.uniquePaxHash, saveCompanionDraft);
  }, [
    currentCompanionDraft,
    isSaveCompanionChecked,
    isSaveCompanionDisabled,
    passenger?.uniquePaxHash,
    saveCompanionDraft,
    setCompanionToCreate,
  ]);

  useEffect(() => {
    if (!isFormInitialized.current) return;

    if (!passenger) return;

    if (isTravelPlannerLocked) return;

    const editedLastName = editedPassenger.updatedFields.lastName;

    const effectiveLastName = firstPassengerLastName
      ? firstPassengerLastName
      : isFirstPassenger && editedLastName !== undefined
        ? editedLastName
        : passenger.lastName;

    setValue('lastName', effectiveLastName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPassengerLastName]);

  useEffect(() => {
    if (!isFormInitialized.current) return;

    if (!passenger) return;

    onIsCustomerChange(isTravelPlanner);

    const effectiveFirstName = isTravelPlanner
      ? stepsData.trip.customerFirstName || passenger.firstName
      : passenger.isCustomer
        ? ''
        : passenger.firstName;
    const effectiveLastName = isTravelPlanner
      ? stepsData.trip.customerLastName || passenger.lastName
      : passenger.isCustomer
        ? ''
        : passenger.lastName;

    setValue('firstName', effectiveFirstName);
    setValue('lastName', effectiveLastName);

    const effectiveDateOfBirth = isTravelPlanner
      ? stepsData.trip.customerDateOfBirth || passenger.dateOfBirth
      : passenger.isCustomer
        ? ''
        : passenger.dateOfBirth;

    setValue('dateOfBirth', effectiveDateOfBirth);

    const effectiveEmail = isTravelPlanner
      ? stepsData.trip.customerEmail || passenger.email
      : passenger.isCustomer
        ? ''
        : passenger.email;

    setValue('email', effectiveEmail);

    const effectivePhone = isTravelPlanner
      ? stepsData.trip.customerPhone || passenger.phone
      : passenger.isCustomer
        ? ''
        : passenger.phone;

    const effectivePhonePrefix = isTravelPlanner
      ? stepsData.trip.customerPhonePrefix || passenger.phonePrefix
      : passenger.isCustomer
        ? ''
        : passenger.phonePrefix;

    setValue('phone', effectivePhone);
    setValue('phonePrefix', effectivePhonePrefix);

    // Manually trigger form state update/validation
    trigger([
      'isCustomer',
      'firstName',
      'lastName',
      'gender',
      'dateOfBirth',
      'email',
      'phone',
      'phonePrefix',
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTravelPlanner]);

  useEffect(() => {
    if (!shouldClear) return;

    updateFormValues();
    trigger();
    setEditedPassenger({
      additionalFields: { hasSaveCompanionChanges: false },
      originalFields: {},
      uniquePaxHash: passenger?.uniquePaxHash || '',
      updatedFields: {},
    });

    if (passenger?.uniquePaxHash) {
      setCompanionToCreate(passenger.uniquePaxHash, null);
    }

    handleCompanionSelectionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClear]);

  useEffect(() => {
    if (!passenger) return;

    initialSaveCompanionCheckedRef.current = isSaveCompanionChecked;

    updateFormValues();
    trigger();
    setEditedPassenger({
      additionalFields: { hasSaveCompanionChanges: false },
      originalFields: {},
      uniquePaxHash: passenger?.uniquePaxHash || '',
      updatedFields: {},
    });
    isFormInitialized.current = true;

    return () => {
      isFormInitialized.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passenger]);

  useEffect(() => {
    onChange?.(editedPassenger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedPassenger]);

  useEffect(() => {
    if (!dataSearchAddress) return;

    setAddressSuggestions(dataSearchAddress.suggestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearchAddress]);

  useEffect(() => {
    if (!dataSearchPostCode) return;

    const suggestion = dataSearchPostCode.suggestions?.[0];

    if (suggestion) {
      setValue('postCode', suggestion.postCode ?? '');
      setValue('postCodePlace', suggestion.postCodePlace ?? '');

      trigger(['postCode', 'postCodePlace']);
    } else {
      setValue('postCodePlace', '');

      trigger(['postCodePlace']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearchPostCode]);

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading)}
      >
        <div className={styles.paxWrapper}>
          <div className={styles.passenger}>
            <SkeletonLoader borderRadius="999px" height="32px" width="32px" />

            <SkeletonLoader
              className={styles.loader}
              height="24px"
              width="120px"
            />
          </div>

          <SkeletonLoader
            className={styles.loader}
            height="24px"
            width="100px"
          />
        </div>

        <div className={styles.nameWrapper}>
          <SkeletonLoader
            className={classNames(styles.firstName, styles.loader)}
            height="52px"
            width="100%"
          />

          <SkeletonLoader
            className={classNames(styles.lastName, styles.loader)}
            height="52px"
            width="100%"
          />
        </div>

        {!hideAge && (
          <div className={styles.ageWrapper}>
            <SkeletonLoader
              className={styles.loader}
              height="54px"
              width="100%"
            />
          </div>
        )}

        {hasContactSection && (
          <div className={styles.contactWrapper}>
            <div className={styles.emailWrapper}>
              <SkeletonLoader
                className={classNames(styles.email, styles.loader)}
                height="52px"
                width="100%"
              />

              <div className={styles.consentLoading}>
                <SkeletonLoader
                  className={styles.loader}
                  height="24px"
                  width="100%"
                />
              </div>
            </div>

            <div className={styles.phoneWrapper}>
              <div className={styles.phoneNumber}>
                <SkeletonLoader
                  className={styles.loader}
                  height="52px"
                  width="100%"
                />

                <SkeletonLoader
                  className={classNames(styles.phone, styles.loader)}
                  height="52px"
                  width="100%"
                />
              </div>

              <div className={styles.consentLoading}>
                <SkeletonLoader
                  className={styles.loader}
                  height="24px"
                  width="100%"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isTravelPlannerLocked]: isTravelPlannerLocked,
      })}
    >
      {passenger && (
        <React.Fragment>
          {(!hideAvatar || showTravelPlannerToggle) && (
            <div className={styles.paxWrapper}>
              {!hideAvatar && (
                <div className={styles.passenger}>
                  <UserAvatar
                    passenger={getBasicPerson()}
                    tooltipPosition={tooltipPosition}
                  />

                  <Typography className={styles.name} tagStyle="bodyMedium">
                    {getNameDisplay()}
                  </Typography>
                </div>
              )}

              {showTravelPlannerToggle && (
                <Radiobutton
                  checked={isTravelPlanner}
                  label={t('items.modules.bookingFlow.travelPlanner')}
                  onChange={() => undefined}
                  onClick={() => {
                    onTravelPlannerToggle?.(
                      isTravelPlanner ? '' : passenger?.uniquePaxHash,
                    );
                  }}
                />
              )}
            </div>
          )}

          <div className={styles.section}>
            {showSectionHeadline && (
              <Typography className={styles.headline} tagStyle="bodySmallBold">
                {t('items.modules.bookingFlow.personalInformation')}
              </Typography>
            )}

            {shouldShowSaveCompanionToggle && companionChipItems.length > 0 && (
              <div className={styles.companionsWrapper}>
                <Typography
                  className={styles.checkbox}
                  tagStyle="bodySmallBold"
                >
                  {t('items.profile.profilePage.companions')}:
                </Typography>

                <CompanionsChipList
                  items={companionChipItems}
                  onClickChip={handleClickCompanion}
                  onClickRemoveChip={handleClickRemoveCompanion}
                />
              </div>
            )}

            <div className={styles.nameWrapper}>
              <Input
                {...register('firstName', { required: true })}
                className={styles.firstName}
                disabled={isTravelPlannerLocked || disableEditNames}
                error={getErrorMessage('firstName', true)}
                id="firstName"
                label={t('items.modules.bookingFlow.firstName')}
                placeholder={passenger.placeholders.firstName}
                required
              />

              <Input
                {...register('lastName', { required: true })}
                className={styles.lastName}
                disabled={
                  isTravelPlannerLocked ||
                  !!firstPassengerLastName ||
                  disableEditNames
                }
                error={getErrorMessage('lastName', true)}
                id="lastName"
                label={t('items.modules.bookingFlow.lastName')}
                placeholder={passenger.placeholders.lastName}
                required
              />
            </div>
          </div>
        </React.Fragment>
      )}

      {!hideAge && (
        <div className={styles.ageWrapper}>
          <Input
            {...register('dateOfBirth', {
              max: {
                message: t('items.form.validationMaximumDate', {
                  date: passenger?.maxDateOfBirth,
                }),
                value: passenger?.maxDateOfBirth || '',
              },
              min: {
                message: t('items.form.validationMinimumDate', {
                  date: passenger?.minDateOfBirth,
                }),
                value: passenger?.minDateOfBirth || '',
              },
              required: true,
            })}
            disabled={isTravelPlannerLocked || disableEditDoB}
            error={getErrorMessage('dateOfBirth', true)}
            id="dateOfBirth"
            label={t('items.modules.bookingFlow.dateOfBirth')}
            max={passenger?.maxDateOfBirth}
            min={passenger?.minDateOfBirth}
            required
            type="date"
          />
        </div>
      )}

      {shouldShowSaveCompanionToggle && (
        <div className={styles.checkboxWrapper}>
          <Checkbox
            checked={isSaveCompanionChecked}
            className={styles.saveCompanionCheckbox}
            disabled={isSaveCompanionDisabled}
            onClick={handleClickSaveCompanion}
          >
            <div className={styles.checkboxLabel}>
              <Typography className={styles.checkbox} tagStyle="bodySmallBold">
                {t('items.profile.profilePage.saveCompanion')}
              </Typography>

              <Icon name="Travelheart" size={24} />
            </div>
          </Checkbox>

          <button
            aria-label={t('items.profile.profilePage.saveCompanion')}
            className={classNames(styles.infoIcon, {
              [styles.isDisabled]: isSaveCompanionDisabled,
            })}
            disabled={isSaveCompanionDisabled}
            onClick={() => setShowSaveCompanionInfoModal(true)}
          >
            <Icon name="Info" />
          </button>
        </div>
      )}

      {hasContactSection && (
        <div className={styles.section}>
          <Typography className={styles.headline} tagStyle="bodySmallBold">
            {t('items.modules.bookingFlow.contactInformation')}
          </Typography>

          <div className={styles.contactWrapper}>
            {(!hideEmailInput || !hideAgreeSendEmailInput) && (
              <div className={styles.emailWrapper}>
                {!hideEmailInput && (
                  <Input
                    {...register('email', {
                      pattern: {
                        message: t('items.modules.bookingFlow.validationEmail'),
                        value: validateEmail,
                      },
                      required: emailAndPhoneMandatory,
                    })}
                    className={styles.email}
                    disabled={isTravelPlannerLocked}
                    error={getErrorMessage('email', emailAndPhoneMandatory)}
                    id="email"
                    label={t('items.modules.bookingFlow.email')}
                    required={emailAndPhoneMandatory}
                    type="email"
                  />
                )}

                {!hideAgreeSendEmailInput && (
                  <div className={styles.agreeSendEmail}>
                    <Checkbox
                      ref={agreeSendEmailRef}
                      checked={agreeSendEmailValue}
                      className={styles.checkbox}
                      disabled={isTravelPlannerLocked}
                      label={agreeSendEmailText}
                      name={agreeSendEmail}
                      onChange={handleAgreeSendEmailChange}
                    />

                    {disclaimerText && (
                      <Tooltip
                        className={styles.tooltip}
                        text={disclaimerText}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {(!hidePhoneInput || !hideAgreeSendSMSInput) && (
              <div className={styles.phoneWrapper}>
                {!hidePhoneInput && (
                  <div className={styles.phoneNumber}>
                    <Select
                      {...register('phonePrefix', {
                        required: emailAndPhoneMandatory,
                      })}
                      disabled={isTravelPlannerLocked}
                      error={getErrorMessage(
                        'phonePrefix',
                        emailAndPhoneMandatory,
                      )}
                      id="phonePrefix"
                      label={t('items.modules.bookingFlow.countryCode')}
                      options={phonePrefixOptions}
                      required={emailAndPhoneMandatory}
                    />

                    <Input
                      {...register('phone', {
                        pattern: {
                          message: t(
                            'items.modules.bookingFlow.validationTelephoneNumber',
                          ),
                          value: validateNumber,
                        },
                        required: emailAndPhoneMandatory,
                      })}
                      className={styles.phone}
                      disabled={isTravelPlannerLocked}
                      error={getErrorMessage('phone', emailAndPhoneMandatory)}
                      id="phone"
                      label={t('items.modules.bookingFlow.telephoneNumber')}
                      required={emailAndPhoneMandatory}
                    />
                  </div>
                )}

                {!hideAgreeSendSMSInput && (
                  <div className={styles.agreeSendSMS}>
                    <Checkbox
                      ref={agreeSendSMSRef}
                      checked={agreeSendSMSValue}
                      className={styles.checkbox}
                      disabled={isTravelPlannerLocked}
                      label={agreeSendSMSText}
                      name={agreeSendSMS}
                      onChange={handleAgreeSendSMSChange}
                    />

                    {disclaimerText && (
                      <Tooltip
                        className={styles.tooltip}
                        text={disclaimerText}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!hideAddressSection && (
        <div className={styles.section}>
          <Typography className={styles.headline} tagStyle="bodySmallBold">
            {t('items.modules.bookingFlow.address')}
          </Typography>

          <div className={styles.addressWrapper}>
            <Select
              {...register('countryId', { required: true })}
              className={styles.countryName}
              error={getErrorMessage('countryId', true)}
              id="countryId"
              label={t('items.modules.bookingFlow.country')}
              options={countryOptions}
              required
            />

            <div className={styles.address}>
              <Controller
                control={control}
                name="address"
                render={({ field }) => (
                  <ComboBox
                    {...field}
                    direction="up"
                    disabled={!hasSelectedCountry}
                    error={getErrorMessage('address', true)}
                    id="passenger-address"
                    label={t('items.modules.bookingFlow.address')}
                    onChange={value => {
                      field.onChange(value);
                      handleSelectSuggestion(value);
                    }}
                    options={addressOptions}
                    required
                    shouldHideOptions={
                      Number(watchedCountryId) !== COUNTRY_ID_DENMARK
                    }
                  />
                )}
                rules={{
                  required: true,
                }}
              />
            </div>

            <div className={styles.addressDetails}>
              <Input
                {...register('postCode', { required: true })}
                className={styles.postCode}
                disabled={!hasSelectedCountry}
                error={getErrorMessage('postCode', true)}
                id="postCode"
                label={t('items.modules.bookingFlow.postCode')}
                required
              />

              <Input
                {...register('postCodePlace', { required: true })}
                className={styles.postCodePlace}
                disabled
                error={getErrorMessage('postCodePlace', true)}
                id="postCodePlace"
                label={t('items.modules.bookingFlow.city')}
                required
              />
            </div>
          </div>
        </div>
      )}

      <SaveCompanionDetailsModal
        isOpen={showSaveCompanionInfoModal}
        onClose={() => setShowSaveCompanionInfoModal(false)}
      />
    </div>
  );
};

EditPaxDetails.displayName = 'EditPaxDetails';

export default EditPaxDetails;
